import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import { v7 as uuidv7 } from "uuid";
import SettingsForm from "./SettingsForm";
import Question from "../commons/model/Question";
import sfetch from "../commons/utils/super-fetch";
import InputState from "../commons/model/form/InputState";
import MaxDistanceInput from "./MaxDistanceInput";
import { Distance } from "../commons/model/address/Distance";
import SettingPracticeQuestionOverrideForm, {
  PracticeQuestionOverrideFormSettings,
} from "./SettingPracticeQuestionOverrideForm";
import SettingsVCQuestionsForm from "./SettingVCQuestionsForm";
import { AvailableEntitiesSettings } from "../commons/model/EntitySettings";
import PatientPracticeSelectionInput from "./PatientPracticeSelectionInput";
import PatientPracticeSelection from "../commons/model/PatientPracticeSelection";

export interface SettingsVirtualConsultationFormProps {
  virtualConsultationFormSettings: VirtualConsultationFormSettings;
  updateUrl: string;
}

interface SettingsVirtualConsultationFormState extends VirtualConsultationFormSettingsUpdate {
  errorMessage?: string;
}

interface VirtualConsultationFormSettings {
  questions?: Question[];
  practiceQuestionOverrideFormSettings?: PracticeQuestionOverrideFormSettings;
  practiceSearchFormSettings?: PracticeSearchFormSettings;
  patientPracticeSelection?: PatientPracticeSelection;
}

interface VirtualConsultationFormSettingsUpdate {
  questions?: Question[];
  practiceQuestionOverrideFormSettings?: PracticeQuestionOverrideFormSettings;
  practiceSearchFormSettings?: PracticeSearchFormSettings;
  patientCanSelectPractice: boolean;
}

interface PracticeSearchFormSettings {
  maxDistance?: Distance;
  defaultMaxDistance: Distance;
}

const SettingsVirtualConsultationForm = ({
  settingsVirtualConsultationForm,
  entities,
  isDsoEmployee,
}: {
  settingsVirtualConsultationForm: SettingsVirtualConsultationFormProps;
  entities: AvailableEntitiesSettings;
  isDsoEmployee: boolean;
}) => {
  const [questionsStates, setQuestionsStates] = useState(new Map<string, InputState>());
  const { virtualConsultationFormSettings } = settingsVirtualConsultationForm;
  const initialState: SettingsVirtualConsultationFormState = {
    questions: virtualConsultationFormSettings.questions
      ? [...virtualConsultationFormSettings.questions]
      : null,
    practiceQuestionOverrideFormSettings:
      virtualConsultationFormSettings.practiceQuestionOverrideFormSettings
        ? {
            ...virtualConsultationFormSettings.practiceQuestionOverrideFormSettings,
          }
        : null,
    practiceSearchFormSettings: virtualConsultationFormSettings.practiceSearchFormSettings,
    patientCanSelectPractice:
      virtualConsultationFormSettings.patientPracticeSelection.patientCanSelectPractice,
  };
  const [state, setState] = useState<SettingsVirtualConsultationFormState>(initialState);
  const {
    questions,
    practiceQuestionOverrideFormSettings,
    practiceSearchFormSettings,
    patientCanSelectPractice,
  } = state;
  const displayPatientPracticeSelection =
    virtualConsultationFormSettings.patientPracticeSelection.displayPatientPracticeSelection;

  const addQuestion = () => {
    setState((prevState) => ({
      ...prevState,
      questions: [...(prevState.questions || []), { uid: uuidv7(), value: "" }],
    }));
  };

  const updateQuestion = (index: number, value: string) => {
    setState((prevState) => {
      const questions = [...(prevState.questions || [])];
      questions[index] = { ...questions[index], value };
      return { ...prevState, questions };
    });
  };

  const updatePracticeOverrideQuestions = (value) => {
    setState({
      ...state,
      practiceQuestionOverrideFormSettings: {
        ...state.practiceQuestionOverrideFormSettings,
        practiceQuestionOverride: value,
      },
    });
  };

  const callUpdatePracticeOverrideQuestionsApi = async (value) => {
    const body = {
      enabled: value,
      entitySettingsId: {
        value: entities.selectedEntity.entitySettingsId.value,
      },
    };
    await sfetch(
      settingsVirtualConsultationForm.virtualConsultationFormSettings
        .practiceQuestionOverrideFormSettings.updatePracticeQuestionOverrideUrl,
      {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
      .then(() =>
        setState((state) => {
          return {
            ...state,
            practiceQuestionOverrideFormSettings: {
              ...state.practiceQuestionOverrideFormSettings,
              practiceQuestionOverride: value,
            },
            errorMessage: null,
          };
        })
      )
      .catch((e) => {
        setState((prevState) => ({
          ...prevState,
          errorMessage: prevState.errorMessage || i18n["Global.Status.technical_error"],
        }));
        throw e;
      });
  };

  const updateDistance = (value: Distance) => {
    setState((state) => {
      return {
        ...state,
        practiceSearchFormSettings: {
          ...state.practiceSearchFormSettings,
          maxDistance: value,
        },
      };
    });
  };

  const updatePatientPracticeSelection = (value: boolean) => {
    setState((state) => {
      return {
        ...state,
        patientCanSelectPractice: value,
      };
    });
  };

  const removeQuestion = (index: number) => {
    const questions = [...state.questions];
    const questionRemoved = questions.splice(index, 1)[0];
    const updatedQuestionsStates = new Map(questionsStates);
    updatedQuestionsStates.delete(questionRemoved.uid);
    setQuestionsStates(updatedQuestionsStates);
    setState({ ...state, questions });
  };

  const cancel = () => {
    setState(initialState);
  };

  const checkValidity = () => {
    return !Array.from(questionsStates.values()).some((it) => it.status.invalid);
  };

  const updateQuestionsStates = (uid: string, inputState: InputState) => {
    setQuestionsStates(new Map(questionsStates.set(uid, inputState)));
  };

  const hasChanged = useMemo(
    () =>
      Object.keys(state).some(
        (field) => JSON.stringify(state[field]) !== JSON.stringify(initialState[field])
      ),
    [state, initialState]
  );

  const save = async () => {
    await sfetch(settingsVirtualConsultationForm.updateUrl, {
      method: "PUT",
      body: JSON.stringify(state),
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (practiceQuestionOverrideFormSettings.updatePracticeQuestionOverrideUrl) {
      await callUpdatePracticeOverrideQuestionsApi(
        state.practiceQuestionOverrideFormSettings.practiceQuestionOverride
      );
    }
    return document.location.reload();
  };

  useEffect(() => {
    Array.from(questionsStates.values()).forEach((it) => {
      const relatedQuestion = state.questions?.find((question) => question.uid === it.name);
      if (relatedQuestion && relatedQuestion.value !== it.value) {
        // Sync value without triggering state updates
        it.setValue(relatedQuestion.value);
      }
    });
  }, [questionsStates]);

  return (
    <SettingsForm
      title={i18n["Page.Settings.VirtualConsultation.Customization.Form.Title"]}
      hasChanges={hasChanged}
      isValid={checkValidity()}
      onCancel={() => cancel()}
      onSave={() => save()}
      id={"virtual-consultation-customisation-form-customisation"}
    >
      {displayPatientPracticeSelection && (
        <>
          <div className="no-label-field-groups">
            <h3 className="title">
              {
                i18n[
                  "Page.Settings.VirtualConsultation.Customization.Form.PatientPracticeSelection.Title"
                ]
              }
            </h3>
            <div className="input-group">
              <PatientPracticeSelectionInput
                patientCanSelectPractice={patientCanSelectPractice}
                onValueChange={updatePatientPracticeSelection}
              />
            </div>
          </div>
          <p className="setting-description">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.PatientPracticeSelection.Description"
              ]
            }
          </p>
          {(practiceSearchFormSettings ||
            isDsoEmployee ||
            practiceQuestionOverrideFormSettings ||
            questions) && <hr />}
        </>
      )}
      {practiceSearchFormSettings && (
        <>
          <p className="setting-description">
            {
              i18n[
                "Page.Settings.VirtualConsultation.Customization.Form.CustomizePracticeResearchRadius.SubTitle"
              ]
            }
          </p>
          <div className="no-label-field-groups">
            <h3 className="title">
              {i18n["Page.Settings.VirtualConsultation.Customization.Form.MaximumDistance.Title"]}
            </h3>
            <div className="input-group">
              <MaxDistanceInput
                defaultDistance={practiceSearchFormSettings.defaultMaxDistance}
                value={practiceSearchFormSettings.maxDistance}
                onValueChange={updateDistance}
              />
            </div>
          </div>
          <hr />
        </>
      )}

      {isDsoEmployee && practiceQuestionOverrideFormSettings && (
        <SettingPracticeQuestionOverrideForm
          practiceQuestionOverrideFormSettings={practiceQuestionOverrideFormSettings}
          onChangeToggle={updatePracticeOverrideQuestions}
        />
      )}
      {practiceQuestionOverrideFormSettings && questions && (
        <SettingsVCQuestionsForm
          canEditQuestions={practiceQuestionOverrideFormSettings.editQuestions}
          questions={questions}
          updateInputStates={updateQuestionsStates}
          addQuestion={addQuestion}
          updateQuestion={updateQuestion}
          removeQuestion={removeQuestion}
        />
      )}
    </SettingsForm>
  );
};

export default SettingsVirtualConsultationForm;
